import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputBase from '@material-ui/core/InputBase';
import ListItem from '@material-ui/core/ListItem';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { confirm } from 'actions/app';
import clsx from 'clsx';
import strings from 'strings';
import { When } from 'UI/components/atoms';

import { useListItemStyles } from './HistoryChatsListItem.styled';

const { menu: MENU_COPIES, deleteConfirm: DELETE_COPIES } = strings.ai.generators.assistant.sidebar;

export const HistoryChatsListItem = ({ chat, isSelected, onClick, onDelete, onPin, onRename }) => {
  const listItemClasses = useListItemStyles();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(chat.sessionTitle);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const toggleEditing = () => setIsEditing(prev => !prev);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handlePinClick = e => {
    e.stopPropagation();
    onPin && onPin(chat.sessionId);
    setMenuAnchorEl(null);
  };

  const handleMenuOpen = e => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = e => {
    e && e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleRenameClick = e => {
    e.stopPropagation();
    setEditedTitle(chat.sessionTitle);
    setIsEditing(true);
    handleMenuClose(e);
  };

  const handleDeleteClick = e => {
    e.stopPropagation();
    dispatch(
      confirm({
        title: DELETE_COPIES.title,
        message: strings.formatString(DELETE_COPIES.message, { chatTitle: chat.sessionTitle }),
        content: <Typography variant="body2">{DELETE_COPIES.helper}</Typography>,
        confirmButtonText: DELETE_COPIES.confirmButtonText,
        cancelButtonText: DELETE_COPIES.cancelButtonText,
        severity: 'error',
        mustComplete: true,
        onConfirm: async ok => {
          if (!ok) return;
          await (onDelete && onDelete(chat.sessionId));
        }
      })
    );
    handleMenuClose(e);
  };

  const handleDoubleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    toggleEditing();
  };

  const handleClick = e => {
    e.stopPropagation();
    onClick && onClick(chat.sessionId);
  };

  const handleTitleChange = e => {
    setEditedTitle(e.target.value);
  };

  const handleRenameSubmit = e => {
    e.stopPropagation();
    if (!editedTitle.trim()) {
      handleRenameCancel(e);
      return;
    }

    onRename && onRename(chat.sessionId, editedTitle);
    toggleEditing();
  };

  const handleRenameCancel = e => {
    e.stopPropagation();
    setEditedTitle(chat.sessionTitle);
    toggleEditing();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleRenameSubmit(e);
    } else if (e.key === 'Escape') {
      handleRenameCancel(e);
    }
    e.stopPropagation();
  };

  return (
    <ListItem
      alignItems="flex-start"
      button={!isEditing}
      className={listItemClasses.root}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      selected={isSelected}
    >
      <Box className={listItemClasses.listItemContent}>
        <When condition={!isEditing}>
          <Typography
            aria-label={chat.sessionTitle}
            className={listItemClasses.chatTitle}
            noWrap
            title={chat.sessionTitle}
            variant="body2"
          >
            {chat.sessionTitle}
          </Typography>
        </When>

        <When condition={isEditing}>
          <ClickAwayListener onClickAway={handleRenameSubmit}>
            <InputBase
              className={listItemClasses.editInput}
              fullWidth
              inputRef={inputRef}
              onChange={handleTitleChange}
              onClick={e => e.stopPropagation()}
              onDoubleClick={e => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              value={editedTitle}
            />
          </ClickAwayListener>
        </When>

        <When condition={!isEditing}>
          <Box className={listItemClasses.actionsContainer}>
            <Box className={listItemClasses.actionSlot}>
              {chat.isPinned && (
                <Box
                  className={clsx(listItemClasses.pinnedIcon, {
                    [listItemClasses.pinnedIconHidden]: isMenuOpen
                  })}
                >
                  <BookmarkIcon color="inherit" fontSize="small" />
                </Box>
              )}
              <IconButton
                aria-label={MENU_COPIES.optionsAriaLabel}
                className={clsx(listItemClasses.menuButton, {
                  [listItemClasses.menuButtonActive]: isMenuOpen
                })}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                onClick={handleMenuOpen}
                size="small"
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </When>
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={e => e.stopPropagation()}
      >
        <MenuItem onClick={handleRenameClick}>{MENU_COPIES.rename}</MenuItem>
        <MenuItem onClick={handlePinClick}>
          {chat.isPinned ? MENU_COPIES.unpinChat : MENU_COPIES.pinChat}
        </MenuItem>
        <MenuItem className={listItemClasses.deleteMenuItem} onClick={handleDeleteClick}>
          {MENU_COPIES.delete}
        </MenuItem>
      </Menu>
    </ListItem>
  );
};
